// services/auth/ApolloAuthService.js
// Apollo Client Integration für Authentication
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  from,
  Observable,
  fromPromise
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { logger } from './Logger';

export class ApolloAuthService {
  constructor(options = {}) {
    this.graphqlEndpoint = options.graphqlEndpoint;
    this.realmService = options.realmService;
    this.tokenService = options.tokenService;
    this.onAuthError = options.onAuthError || (() => {});
    this.pendingRequests = [];
    this.isRefreshing = false;
    this.latestAccessToken = null; // Speichern des neuesten Tokens
    this.tokenMetrics = {}; // Für Token-Monitoring

    logger.debug('ApolloAuthService initialized');

    // Event-Listener für Token-Aktualisierungen hinzufügen
    if (options.authService) {
      this.authService = options.authService;
      this.authService.addEventListener(
        'apollo:tokenRefreshed',
        this._handleTokenRefreshed.bind(this)
      );
      logger.debug('Registered for apollo:tokenRefreshed events');
    }
  }

  // Validiert einen Token auf korrektes JWT-Format
  _validateToken(token, source = 'unknown') {
    if (!token || typeof token !== 'string') {
      logger.error(
        `Invalid token from ${source}: Token is null or not a string`
      );
      return false;
    }

    // Token-Format prüfen
    const isValid = this.tokenService.isValidJwtFormat(token);

    // Token-Typ prüfen
    if (isValid) {
      const tokenType = this.tokenService.getTokenType(token);
      if (tokenType === 'invalid') {
        logger.error(
          `Token from ${source} has valid format but invalid content`
        );
        return false;
      }

      logger.debug(`Token from ${source} validated: type=${tokenType}`);
    } else {
      logger.error(`Token from ${source} has invalid JWT format`);
    }

    return isValid;
  }

  // Überwacht Token-Metriken für Analyse
  _monitorToken(token, source) {
    if (!token) {
      logger.warn(`Null token from source: ${source}`);
      return;
    }

    const segments = token.split('.');
    logger.debug(
      `Token from ${source}: ${
        segments.length
      } segments, first 15 chars: ${token.substring(0, 15)}...`
    );

    // Speichern von Token-Metriken für Analyse
    this.tokenMetrics[source] = {
      timestamp: Date.now(),
      segments: segments.length,
      length: token.length,
      type: this.tokenService.getTokenType(token)
    };

    // Warnung bei Inkonsistenzen
    const metrics = Object.values(this.tokenMetrics);
    if (metrics.length > 1) {
      const segmentCounts = new Set(metrics.map((m) => m.segments));
      if (segmentCounts.size > 1) {
        logger.warn(
          `Inconsistent token formats detected: ${Array.from(
            segmentCounts
          ).join(', ')} segments`
        );
      }

      const tokenTypes = new Set(metrics.map((m) => m.type));
      if (tokenTypes.size > 1) {
        logger.warn(
          `Inconsistent token types detected: ${Array.from(tokenTypes).join(
            ', '
          )}`
        );
      }
    }
  }

  // Einheitliche Methode zum Abrufen eines gültigen Auth-Tokens für GraphQL-Anfragen
  // WICHTIG: Für GraphQL-Anfragen muss das Realm Access Token verwendet werden!
  async getAuthToken() {
    logger.debug('Getting valid Realm access token for GraphQL request');

    // Versuchen, den neuesten Access Token zu verwenden
    if (
      this.latestAccessToken &&
      this._validateToken(this.latestAccessToken, 'latest_token_cache')
    ) {
      this._monitorToken(this.latestAccessToken, 'latest_token_cache');
      logger.debug('Using cached access token for GraphQL request');
      console.log(
        'USING CACHED ACCESS TOKEN FROM MEMORY:',
        this.latestAccessToken
      );
      return this.latestAccessToken;
    }

    // Versuchen, den Token aus dem TokenService zu holen
    if (this.tokenService) {
      const cachedToken = this.tokenService.getToken('accessToken');
      if (
        cachedToken &&
        this._validateToken(cachedToken, 'token_service_cache')
      ) {
        this._monitorToken(cachedToken, 'token_service_cache');
        logger.debug('Using cached access token for GraphQL request');
        console.log(
          'USING CACHED ACCESS TOKEN FROM TOKEN SERVICE:',
          cachedToken
        );
        return cachedToken;
      }
    }

    // Wenn kein gültiges Access Token im Cache ist, versuchen wir, die Tokens zu aktualisieren
    try {
      logger.debug('No valid access token in cache, refreshing tokens');
      const refreshSuccess = await this.tokenService.refreshTokens(true);

      if (refreshSuccess) {
        // Nach erfolgreicher Aktualisierung erneut versuchen, das Access Token zu holen
        const refreshedToken = this.tokenService.getToken('accessToken');
        if (
          refreshedToken &&
          this._validateToken(refreshedToken, 'refreshed_token')
        ) {
          this._monitorToken(refreshedToken, 'refreshed_token');
          logger.debug('Using refreshed access token for GraphQL request');
          console.log('USING REFRESHED ACCESS TOKEN:', refreshedToken);
          return refreshedToken;
        }
      }

      // Wenn die Aktualisierung fehlschlägt oder kein gültiges Token zurückgibt,
      // versuchen wir, uns neu zu authentifizieren
      logger.debug(
        'Token refresh failed or no valid access token after refresh, attempting recovery'
      );
      await this._recoverFromTokenError();

      // Nach der Wiederherstellung erneut versuchen, das Access Token zu holen
      const recoveredToken = this.tokenService.getToken('accessToken');
      if (
        recoveredToken &&
        this._validateToken(recoveredToken, 'recovered_token')
      ) {
        this._monitorToken(recoveredToken, 'recovered_token');
        logger.debug('Using recovered access token for GraphQL request');
        return recoveredToken;
      }

      // Wenn alles fehlschlägt, müssen wir uns mit dem Fallback-Mechanismus anmelden
      logger.error(
        'No valid access token available after recovery, using fallback'
      );

      // Fallback: Anonyme Anmeldung oder andere Fallback-Mechanismen
      // Hinweis: Anonyme Anmeldung sollte nur als letzter Ausweg verwendet werden
      await this.realmService.loginAnonymously();

      // Für anonyme Benutzer verwenden wir das Realm Access Token
      const anonymousToken = await this.realmService.getValidAccessToken();
      this._monitorToken(anonymousToken, 'anonymous_fallback');
      logger.debug('Using anonymous token for GraphQL request (fallback)');
      console.log('USING ANONYMOUS TOKEN FOR APOLLO CLIENT:', anonymousToken);
      return anonymousToken;
    } catch (error) {
      logger.error('Error getting auth token for GraphQL request', error);

      // Bei kritischen Fehlern zur Login-Seite weiterleiten
      this.onAuthError();
      throw error;
    }
  }

  // Wiederherstellung bei Token-Fehlern
  async _recoverFromTokenError() {
    logger.debug('Attempting to recover from token error');

    // Alle Token-Caches leeren
    if (this.tokenService) {
      this.tokenService.clearAllTokens();
    }

    this.latestAccessToken = null;

    // Versuchen, die Sitzung neu zu initialisieren
    try {
      // Wenn ein Auth-Service verfügbar ist, versuchen, Tokens zu aktualisieren
      if (this.authService) {
        const success = await this.authService.refreshTokens(true);
        if (success) {
          logger.debug('Successfully recovered by refreshing tokens');
          return true;
        }
      }

      // Wenn das nicht funktioniert, anonym anmelden
      await this.realmService.loginAnonymously();
      logger.debug('Recovered by anonymous login');
      return true;
    } catch (error) {
      logger.error('Failed to recover from token error', error);
      return false;
    }
  }

  // Handler für Token-Aktualisierungen
  _handleTokenRefreshed(data) {
    logger.debug(`Received token refresh event with new tokens`);

    // Access Token validieren und speichern
    if (
      data.accessToken &&
      this._validateToken(data.accessToken, 'access_token_refresh_event')
    ) {
      // Token-Metriken überwachen
      this._monitorToken(data.accessToken, 'access_token_refresh_event');

      // Neuesten Access Token speichern
      this.latestAccessToken = data.accessToken;

      // Access Token auch im TokenService speichern
      if (this.tokenService) {
        this.tokenService.setToken('accessToken', data.accessToken);
        logger.debug(
          `Updated access token in TokenService: ${data.accessToken.substring(
            0,
            15
          )}...`
        );

        // Ausführliches Logging des empfangenen Access Tokens
        console.log(
          'RECEIVED REFRESHED ACCESS TOKEN IN APOLLO SERVICE:',
          data.accessToken
        );
      }

      logger.debug(
        `Updated latest access token in ApolloAuthService: ${data.accessToken.substring(
          0,
          15
        )}...`
      );
    } else if (data.accessToken) {
      logger.error('Received invalid access token in refresh event, ignoring');
    }

    // ID-Token validieren und im TokenService speichern, wenn vorhanden
    if (
      data.idToken &&
      this._validateToken(data.idToken, 'id_token_refresh_event')
    ) {
      // Token-Metriken überwachen
      this._monitorToken(data.idToken, 'id_token_refresh_event');

      // ID-Token im TokenService speichern
      if (this.tokenService) {
        this.tokenService.setToken('idToken', data.idToken);
        logger.debug(
          `Updated ID token in TokenService: ${data.idToken.substring(
            0,
            15
          )}...`
        );

        // Ausführliches Logging des empfangenen ID-Tokens
        console.log(
          'RECEIVED REFRESHED ID TOKEN IN APOLLO SERVICE:',
          data.idToken
        );
      }
    } else if (data.idToken) {
      logger.error('Received invalid ID token in refresh event, ignoring');
    }
  }

  // Apollo Client erstellen
  createApolloClient() {
    logger.debug('Creating Apollo client');

    // 1. HTTP-Link für GraphQL-Anfragen
    const httpLink = new HttpLink({
      uri: this.graphqlEndpoint,
      fetch: async (uri, options) => {
        try {
          // Einheitliche Methode zum Abrufen eines gültigen Realm Access Tokens verwenden
          // WICHTIG: Für GraphQL-Anfragen muss das Realm Access Token verwendet werden!
          const accessToken = await this.getAuthToken();

          // Ausführliches Logging des tatsächlichen Tokens, das für Apollo-Anfragen verwendet wird
          console.log(
            'ACTUAL TOKEN USED FOR APOLLO CLIENT REQUEST:',
            accessToken
          );

          logger.debug(
            `Apollo fetch handler using validated Realm access token: ${accessToken.substring(
              0,
              15
            )}...`
          );

          // Authorization-Header mit Realm Access Token hinzufügen (bestehenden Header entfernen)
          if (options.headers.Authorization) {
            logger.debug(
              `Removing existing Authorization header: ${options.headers.Authorization}`
            );
          }
          delete options.headers.Authorization;
          options.headers.Authorization = `Bearer ${accessToken}`;
          logger.debug(
            `Set Authorization header with Realm access token: ${options.headers.Authorization.substring(
              0,
              20
            )}...`
          );

          // Anfrage ausführen
          return fetch(uri, options);
        } catch (error) {
          logger.error('Error in Apollo fetch handler', error);

          // Bei Token-Fehlern Wiederherstellung versuchen
          if (
            error.message &&
            (error.message.includes('token') ||
              error.message.includes('auth') ||
              error.message.includes('unauthorized'))
          ) {
            logger.debug('Token-related error detected, attempting recovery');
            try {
              await this._recoverFromTokenError();

              // Nach Wiederherstellung neuen Token holen
              // Nach Wiederherstellung Realm Access Token verwenden
              const recoveredToken = await this.getAuthToken();
              if (options.headers.Authorization) {
                logger.debug(
                  `Removing existing Authorization header after recovery: ${options.headers.Authorization}`
                );
              }
              delete options.headers.Authorization;
              options.headers.Authorization = `Bearer ${recoveredToken}`;
              logger.debug(
                `Set Authorization header after recovery with Realm access token: ${options.headers.Authorization.substring(
                  0,
                  20
                )}...`
              );

              return fetch(uri, options);
            } catch (recoveryError) {
              logger.error(
                'Recovery failed, proceeding with original error',
                recoveryError
              );
            }
          }

          throw error;
        }
      }
    });

    // 2. Error-Link für die Behandlung von Auth-Fehlern
    const errorLink = onError(
      ({ graphQLErrors, networkError, operation, forward }) => {
        // Auth-Fehler im GraphQL-Response
        if (graphQLErrors) {
          const authError = graphQLErrors.find(
            (error) =>
              error.extensions?.code === 'UNAUTHENTICATED' ||
              error.message.includes('auth') ||
              error.message.includes('token') ||
              error.message.includes('unauthorized')
          );

          if (authError) {
            logger.debug('GraphQL auth error detected:', authError.message);

            // Token-Aktualisierung mit Warteschlange
            return this._handleTokenRefresh(operation, forward);
          }
        }

        // Network-Fehler prüfen (z.B. 401 Unauthorized)
        if (
          networkError &&
          'statusCode' in networkError &&
          (networkError.statusCode === 401 || networkError.statusCode === 403)
        ) {
          logger.debug(`Network ${networkError.statusCode} error detected`);

          // Token-Aktualisierung mit Warteschlange
          return this._handleTokenRefresh(operation, forward);
        }
      }
    );

    // 3. Request-Link für Pre-Flight Token-Prüfung
    const requestLink = new ApolloLink((operation, forward) => {
      // Prüfen, ob Tokens aktualisiert werden müssen
      if (
        this.tokenService &&
        this.tokenService.shouldRefreshToken('accessToken')
      ) {
        logger.debug('Token refresh needed before request');

        // Wenn bereits eine Aktualisierung läuft, zur Warteschlange hinzufügen
        if (this.isRefreshing) {
          logger.debug('Token refresh already in progress, queueing request');

          return new Observable((observer) => {
            this.pendingRequests.push(() => {
              const subscription = forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              });

              return () => subscription.unsubscribe();
            });
          });
        }

        this.isRefreshing = true;

        // Token aktualisieren und dann fortfahren
        return fromPromise(
          this.tokenService.refreshTokens().finally(() => {
            this.isRefreshing = false;
            this._resolvePendingRequests();
          })
        ).flatMap(() => forward(operation));
      }

      // Direkt fortfahren, wenn keine Aktualisierung nötig ist
      return forward(operation);
    });

    // 4. Apollo-Client erstellen
    // Korrigierte Reihenfolge der Links: requestLink -> httpLink -> errorLink
    // Dies stellt sicher, dass der errorLink erst nach dem httpLink ausgeführt wird,
    // um Fehler zu behandeln, die vom Server zurückgegeben werden
    return new ApolloClient({
      link: from([requestLink, httpLink, errorLink]),
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all'
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        },
        mutate: {
          errorPolicy: 'all'
        }
      },
      // Performance-Optimierungen
      assumeImmutableResults: true, // Reduziert unnötige Vergleichsoperationen
      queryDeduplication: true, // Verhindert doppelte Anfragen
      connectToDevTools: true
    });
  }

  // Hilfsmethode zur Behandlung von Token-Aktualisierungen bei Auth-Fehlern
  _handleTokenRefresh(operation, forward) {
    // Wenn bereits eine Aktualisierung läuft, zur Warteschlange hinzufügen
    if (this.isRefreshing) {
      logger.debug('Token refresh already in progress, queueing request');

      return new Observable((observer) => {
        this.pendingRequests.push(() => {
          const subscription = forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          });

          return () => subscription.unsubscribe();
        });
      });
    }

    this.isRefreshing = true;

    return fromPromise(
      this.tokenService
        .refreshTokens(true) // Erzwungene Aktualisierung
        .then(async (success) => {
          logger.debug(`Token refresh completed, success: ${success}`);

          if (!success) {
            // Bei Fehlschlag Wiederherstellung versuchen
            logger.debug('Token refresh failed, attempting recovery');
            const recoverySuccess = await this._recoverFromTokenError();

            if (!recoverySuccess) {
              // Bei Fehlschlag Auth-Error-Callback aufrufen
              this.onAuthError();
              throw new Error('Authentication failed');
            }
          }

          // Einheitliche Methode zum Abrufen eines gültigen Tokens verwenden
          const accessToken = await this.getAuthToken();

          // Ausführliches Logging des tatsächlichen Tokens, das nach Token-Aktualisierung verwendet wird
          console.log('ACTUAL TOKEN USED AFTER TOKEN REFRESH:', accessToken);

          if (accessToken) {
            // Token validieren
            if (!this._validateToken(accessToken, 'token_refresh_handler')) {
              logger.error('Invalid token after refresh, attempting recovery');
              await this._recoverFromTokenError();
              const recoveredToken = await this.getAuthToken();

              // Ausführliches Logging des tatsächlichen Tokens, das nach Wiederherstellung verwendet wird
              console.log('ACTUAL TOKEN USED AFTER RECOVERY:', recoveredToken);

              if (
                !this._validateToken(recoveredToken, 'token_refresh_recovery')
              ) {
                this.onAuthError();
                throw new Error('Unable to obtain valid token after recovery');
              }

              // Token-Metriken überwachen
              this._monitorToken(recoveredToken, 'token_refresh_recovery');

              operation.setContext(({ headers = {} }) => {
                // Neues Headers-Objekt ohne den Authorization-Header erstellen
                const newHeaders = { ...headers };
                if (newHeaders.Authorization) {
                  logger.debug(
                    `Removing existing Authorization header in setContext (recovery): ${newHeaders.Authorization}`
                  );
                }
                delete newHeaders.Authorization;

                // Neuen Authorization-Header hinzufügen
                return {
                  headers: {
                    ...newHeaders,
                    Authorization: `Bearer ${recoveredToken}`
                  }
                };
              });
            } else {
              // Token-Metriken überwachen
              this._monitorToken(accessToken, 'token_refresh_handler');

              operation.setContext(({ headers = {} }) => {
                // Neues Headers-Objekt ohne den Authorization-Header erstellen
                const newHeaders = { ...headers };
                if (newHeaders.Authorization) {
                  logger.debug(
                    `Removing existing Authorization header in setContext: ${newHeaders.Authorization}`
                  );
                }
                delete newHeaders.Authorization;

                // Neuen Authorization-Header hinzufügen
                return {
                  headers: {
                    ...newHeaders,
                    Authorization: `Bearer ${accessToken}`
                  }
                };
              });
            }
          } else {
            logger.error('No access token available after token refresh');
            this.onAuthError();
            throw new Error('No access token available');
          }

          return true;
        })
        .finally(() => {
          this.isRefreshing = false;
          this._resolvePendingRequests();
        })
    ).flatMap(() => forward(operation));
  }

  // Hilfsmethode zum Fortsetzen ausstehender Anfragen
  _resolvePendingRequests() {
    logger.debug(`Resolving ${this.pendingRequests.length} pending requests`);

    // Kopie der Warteschlange erstellen, um Race Conditions zu vermeiden
    const pendingRequestsCopy = [...this.pendingRequests];
    this.pendingRequests = [];

    pendingRequestsCopy.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        logger.error('Error executing pending request callback', error);
      }
    });
    this.pendingRequests = [];
  }
}
